import { useState, useCallback, useEffect } from 'react';
import { Alert, Button } from '@nula/core-ui';
import { useWorkspaceStatus } from '@/lib/sync/hooks/use-workspace-status';
import { cva } from 'class-variance-authority';
import styles from './storage-degraded-banner.module.css';

const storageDegradedBanner = cva(styles.base);

/**
 * StorageDegradedBanner — displayed when IndexedDB quota is exceeded.
 *
 * - Reads degraded state from useWorkspaceStatus()
 * - Dismissible per session (resets on reload)
 * - Full-width banner mode using core-ui Alert
 * - Accessible: role="alert", keyboard-dismissible (Escape key)
 */
export function StorageDegradedBanner() {
  const status = useWorkspaceStatus();
  const [dismissed, setDismissed] = useState(false);

  const isDegraded =
    (status.phase === 'local-ready' || status.phase === 'sync-complete') &&
    status.persistence === 'degraded';

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Keyboard accessibility: Escape to dismiss
  useEffect(() => {
    if (!isDegraded || dismissed) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDegraded, dismissed, handleDismiss]);

  if (!isDegraded || dismissed) return null;

  return (
    <Alert
      type="warning"
      banner
      showIcon
      message="Storage is full. Your work still syncs to the cloud but won't be saved offline."
      action={
        <Button
          variant="text"
          size="sm"
          aria-label="Dismiss storage warning"
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      }
      className={storageDegradedBanner()}
      style={{ '--storage-banner-min-height': '44px' } as React.CSSProperties}
    />
  );
}
